import jsPDF from 'jspdf';

/**
 * Utilitários para exportação de dados do chat em diferentes formatos
 */

// Tipos
interface ExportData {
  resposta: string;
  dadosBrutos?: Record<string, any>[];
  titulo?: string;
}

/**
 * Remove marcadores de formatação do texto
 */
const cleanText = (text: string): string => {
  return text
    .replace(/__PARAGRAPH_BREAK__/g, '\n\n')
    .replace(/__LINE_BREAK__/g, '\n')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/_([^_]+)_/g, '$1');
};

/**
 * Formata dados para CSV
 */
const dataToCSV = (data: Record<string, any>[]): string => {
  if (!data || data.length === 0) return '';

  const headers = Object.keys(data[0]);
  const csvRows: string[] = [];

  // Header
  csvRows.push(headers.join(';'));

  // Rows
  data.forEach(row => {
    const values = headers.map(header => {
      const val = row[header];
      if (val === null || val === undefined) return '';
      const strVal = String(val);
      // Escape valores com ponto-e-vírgula ou aspas
      if (strVal.includes(';') || strVal.includes('"') || strVal.includes('\n')) {
        return `"${strVal.replace(/"/g, '""')}"`;
      }
      return strVal;
    });
    csvRows.push(values.join(';'));
  });

  return csvRows.join('\n');
};

/**
 * Gera e baixa arquivo TXT
 */
export const exportToTXT = (data: ExportData): void => {
  const titulo = data.titulo || 'Análise do Chat';
  const dataAtual = new Date().toLocaleString('pt-BR');

  let content = `${'='.repeat(60)}\n`;
  content += `${titulo.toUpperCase()}\n`;
  content += `${'='.repeat(60)}\n\n`;
  content += `Data de geração: ${dataAtual}\n\n`;
  content += `${'─'.repeat(60)}\n`;
  content += `RESPOSTA DA IA\n`;
  content += `${'─'.repeat(60)}\n\n`;
  content += cleanText(data.resposta);
  content += '\n\n';

  if (data.dadosBrutos && data.dadosBrutos.length > 0) {
    content += `${'─'.repeat(60)}\n`;
    content += `DADOS (${data.dadosBrutos.length} registros)\n`;
    content += `${'─'.repeat(60)}\n\n`;

    const headers = Object.keys(data.dadosBrutos[0]);
    content += `Colunas: ${headers.join(' | ')}\n\n`;

    data.dadosBrutos.forEach((row, idx) => {
      content += `[${idx + 1}] `;
      const values = headers.map(h => `${h}: ${row[h] ?? '-'}`);
      content += values.join(' | ');
      content += '\n';
    });
  }

  content += `\n${'='.repeat(60)}\n`;
  content += `Gerado por Especialistas Admin\n`;

  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `analise-chat-${Date.now()}.txt`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Gera e baixa arquivo CSV (Excel)
 */
export const exportToExcel = (data: ExportData): void => {
  if (!data.dadosBrutos || data.dadosBrutos.length === 0) {
    // Se não há dados brutos, exporta a resposta em formato simples
    const content = `Resposta\n"${cleanText(data.resposta).replace(/"/g, '""')}"`;
    const blob = new Blob([`\ufeff${content}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `analise-chat-${Date.now()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    return;
  }

  const csvContent = dataToCSV(data.dadosBrutos);
  // BOM para Excel reconhecer UTF-8
  const blob = new Blob([`\ufeff${csvContent}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `dados-chat-${Date.now()}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Gera e baixa arquivo PDF
 */
export const exportToPDF = (data: ExportData): void => {
  const titulo = data.titulo || 'Análise do Chat';
  const dataAtual = new Date().toLocaleString('pt-BR');

  // eslint-disable-next-line new-cap
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  const maxWidth = pageWidth - margin * 2;
  let yPos = 20;

  // Título
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(titulo, margin, yPos);
  yPos += 10;

  // Data
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100);
  doc.text(`Gerado em: ${dataAtual}`, margin, yPos);
  yPos += 15;

  // Linha separadora
  doc.setDrawColor(200);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 10;

  // Resposta
  doc.setTextColor(0);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Resposta da IA', margin, yPos);
  yPos += 8;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);

  const respostaLimpa = cleanText(data.resposta);
  const linhas = doc.splitTextToSize(respostaLimpa, maxWidth);

  linhas.forEach((linha: string) => {
    if (yPos > 270) {
      doc.addPage();
      yPos = 20;
    }
    doc.text(linha, margin, yPos);
    yPos += 6;
  });

  // Dados brutos (se existirem)
  if (data.dadosBrutos && data.dadosBrutos.length > 0) {
    yPos += 10;

    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }

    doc.setDrawColor(200);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 10;

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(`Dados (${data.dadosBrutos.length} registros)`, margin, yPos);
    yPos += 10;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');

    // Mostrar primeiros 20 registros
    const dadosLimitados = data.dadosBrutos.slice(0, 20);
    const headers = Object.keys(dadosLimitados[0]);

    dadosLimitados.forEach((row, idx) => {
      if (yPos > 270) {
        doc.addPage();
        yPos = 20;
      }

      const rowText = `${idx + 1}. ${headers.map(h => `${h}: ${row[h] ?? '-'}`).join(' | ')}`;
      const rowLinhas = doc.splitTextToSize(rowText, maxWidth);

      rowLinhas.forEach((linha: string) => {
        if (yPos > 270) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(linha, margin, yPos);
        yPos += 5;
      });
      yPos += 2;
    });

    if (data.dadosBrutos.length > 20) {
      yPos += 5;
      doc.setTextColor(100);
      doc.text(`... e mais ${data.dadosBrutos.length - 20} registros.`, margin, yPos);
      doc.setTextColor(0);
    }
  }

  // Footer
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(`Página ${i} de ${pageCount}`, pageWidth / 2, 290, { align: 'center' });
    doc.text('Especialistas Admin', margin, 290);
  }

  doc.save(`analise-chat-${Date.now()}.pdf`);
};
